import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import { NEW_DISTINCTIVE_RINGTONE } from "@/constants/New";
import urls from "@/constants/Urls";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputMedia from "@/design/forms/InputMedia";
import InputText from "@/design/forms/InputText";
import InputToggle from "@/design/forms/InputToggle";
import { showSnackbar } from "@/store/reducers/app";
import { number, string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowLeft, viBel, viPlus, viSpinnerThird, viTrashCan } from "@sippy-platform/valkyrie";

import {
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import distinctiveRingtoneReducer from "./distinctiveRingtoneReducer";

function DistinctiveRingtone() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));

  const pbx = useSelector((state) => state.domain.pbx);
  const ringtoneStore = useSelector((state) => state.domain.ringtones);

  // Get general data
  useEffect(() => {
    Promise.resolve(Api.getRingtones());
  }, []);

  const {
    value: distinctiveRingtone,
    dispatch: distinctiveRingtoneDispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_DISTINCTIVE_RINGTONE,
    reducer: distinctiveRingtoneReducer,
    validator: () => ({
      name: string()
        .required({ message: "Name is required." })
        .length([1, 255], { message: "Name must be at most 255 characters." }),
      ringtoneId: number().required({ message: "Ringtone is required." }),
    }),
    onSubmit: () => Api.storeDistinctiveRingtone(distinctiveRingtone),
    onSuccess: (response) => {
      if (!distinctiveRingtone.id) {
        distinctiveRingtoneDispatch({
          type: "id",
          value: response.data.distinctiveRingtone.id,
        });
        navigate(`../${response.data.distinctiveRingtone.id}`);
      }

      Promise.resolve(Api.getDistinctiveRingtones());
    },
  });

  useEffect(() => {
    let ignore = false;

    if (Number(id) === distinctiveRingtone.id || !id) {
      return;
    }

    if (id === "new") {
      Api.cancelActiveGetRequests();
      distinctiveRingtoneDispatch({
        type: "set",
        value: NEW_DISTINCTIVE_RINGTONE,
      });

      return () => {
        ignore = true;
      };
    }

    toggleLoadingState(true);

    Promise.resolve(Api.getDistinctiveRingtone({ id: Number(id) }))
      .then(
        (response) =>
          !ignore &&
          distinctiveRingtoneDispatch({
            type: "set",
            value: response.data.distinctiveRingtone,
          }),
      )
      .finally(() => toggleLoadingState(false));

    return () => {
      ignore = true;
    };
  }, [distinctiveRingtone.id, distinctiveRingtoneDispatch, id]);

  // Handle changes
  const formHandler = (property, value) => {
    distinctiveRingtoneDispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  // Delete
  function onDeleteFinish() {
    Promise.resolve(Api.getDistinctiveRingtones()).then(() => navigate(".."));
  }

  // Apply
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [applyingDistinctiveRingtone, setApplyingDistinctiveRingtone] = useState(false);

  function applyDistinctiveRingtone() {
    setApplyingDistinctiveRingtone(true);
    setShowApplyDialog(false);

    Promise.resolve(Api.applyDistinctiveRingtone(distinctiveRingtone))
      .then(() => {
        dispatch(showSnackbar("Distinctive ringtone successfully applied."));
        setApplyingDistinctiveRingtone(false);
      })
      .finally(() => setApplyingDistinctiveRingtone(false));
  }

  // Prune
  const [showPruneDialog, setShowPruneDialog] = useState(false);
  const [pruningDistinctiveRingtone, setPruningDistinctiveRingtone] = useState(false);

  function pruneDistinctiveRingtone() {
    setPruningDistinctiveRingtone(true);
    setShowPruneDialog(false);

    Promise.resolve(Api.pruneDistinctiveRingtone(distinctiveRingtone))
      .then(() => {
        dispatch(showSnackbar("Distinctive ringtone successfully pruned."));
        setPruningDistinctiveRingtone(false);
      })
      .finally(() => setPruningDistinctiveRingtone(false));
  }

  return (
    <Container>
      <title>{`${distinctiveRingtone.name || "Distinctive ringtone"} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          avatar={
            !matches ? (
              <IconButton variant="avatar" component={NavLink} to={".."}>
                <Valkyrie icon={viArrowLeft} /> <Avatar text={distinctiveRingtone.name} icon={viBel} />
              </IconButton>
            ) : (
              <Avatar text={distinctiveRingtone.name} icon={viBel} />
            )
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              <DeleteFlow
                advanced
                key={distinctiveRingtone.id || "nk"}
                isActionable={!!distinctiveRingtone?.id}
                onDeleteCheck={() => Api.deleteCheckDistinctiveRingtone(distinctiveRingtone)}
                onDelete={() => Api.deleteDistinctiveRingtone(distinctiveRingtone)}
                name={`${distinctiveRingtone.name}`}
                onDeleteFinish={onDeleteFinish}
              />
            </>
          }
          title={distinctiveRingtone.name || "Unnamed distinctive ringtone"}
        />
        <FormGroup title="Identity">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText
              required
              id="name"
              label="Name"
              onChange={formHandler}
              value={distinctiveRingtone.name}
              error={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputMedia
              required
              id="ringtoneId"
              label="Ringtone"
              onChange={formHandler}
              value={distinctiveRingtone.ringtoneId}
              selectValue="id"
              selects={ringtoneStore.list}
              error={errors}
              getOptionLabel={(option) => `${option.brand} ${option.ringtone}`}
              getSourceDependency={(option) => option?.token}
              getSourceGeneration={(option) =>
                `${urls.audio}${encodeURIComponent(`ringtone/${option.brand.toLowerCase()}/`)}${option.ringtone}?alt=media&token=${option.token}`
              }
            />
          </Grid>
        </FormGroup>
        <FormGroup title="Settings">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputToggle
              id="defaultForNewAccounts"
              error={errors}
              onChange={formHandler}
              states={[false, true]}
              value={distinctiveRingtone.defaultForNewAccounts}
              primary="Default for new users"
              secondary="Add this distinctive ringtone to all new users."
            />
          </Grid>
        </FormGroup>
        {distinctiveRingtone?.id && (
          <FormGroup title="Manage">
            <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
              <Typography sx={{ mb: 1 }}>Apply ringtone to all existing users</Typography>
              <Button
                color="primary"
                variant="contained"
                onClick={() => setShowApplyDialog(true)}
                startIcon={
                  <Valkyrie
                    icon={applyingDistinctiveRingtone ? viSpinnerThird : viPlus}
                    spin={applyingDistinctiveRingtone}
                  />
                }
              >
                Apply
              </Button>
              <Dialog open={showApplyDialog} onClose={() => setShowApplyDialog(false)} scroll="paper">
                <Stack
                  sx={{
                    gap: 2,
                  }}
                >
                  <DialogTitle sx={{ p: 0 }}>Apply {distinctiveRingtone.name}</DialogTitle>
                  <DialogContent sx={{ p: 0 }}>
                    <Typography>
                      Are you sure you want to apply this distinctive ringtone to all users? Any users who have already
                      reached the limit of available distinctive ringtones will not get his ringtone applied to them.
                    </Typography>
                  </DialogContent>
                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Button onClick={() => setShowApplyDialog(false)} variant="outlined">
                      Cancel
                    </Button>
                    <Button
                      onClick={() => applyDistinctiveRingtone()}
                      autoFocus
                      variant="contained"
                      startIcon={<Valkyrie icon={viPlus} />}
                    >
                      Apply
                    </Button>
                  </Stack>
                </Stack>
              </Dialog>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
              <Typography sx={{ mb: 1 }}>Prune ringtone from all users</Typography>
              <Button
                color="primary"
                variant="contained"
                onClick={() => setShowPruneDialog(true)}
                startIcon={
                  <Valkyrie
                    icon={pruningDistinctiveRingtone ? viSpinnerThird : viTrashCan}
                    spin={pruningDistinctiveRingtone}
                  />
                }
              >
                Prune
              </Button>
              <Dialog open={showPruneDialog} onClose={() => setShowPruneDialog(false)} scroll="paper">
                <Stack
                  sx={{
                    gap: 2,
                  }}
                >
                  <DialogTitle sx={{ p: 0 }}>Prune {distinctiveRingtone.name}</DialogTitle>
                  <DialogContent sx={{ p: 0 }}>
                    <Typography>
                      Are you sure you want to prune this distinctive ringtone? It will be removed from all users who
                      have set it as an available ringtone.
                    </Typography>
                  </DialogContent>
                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Button onClick={() => setShowPruneDialog(false)} variant="outlined">
                      Cancel
                    </Button>
                    <Button
                      onClick={() => pruneDistinctiveRingtone()}
                      autoFocus
                      variant="contained"
                      color="error"
                      startIcon={<Valkyrie icon={viTrashCan} />}
                    >
                      Prune
                    </Button>
                  </Stack>
                </Stack>
              </Dialog>
            </Grid>
          </FormGroup>
        )}
      </Stack>
    </Container>
  );
}

export default DistinctiveRingtone;
